<template>
  <div class="conversation">
    <h3 class="container-title">
      {{ $t('page.active_conversation.my_conversations') }}
    </h3>
    <div v-for="conversation of conversations" :key="conversation.id" class="conversation-row-container">
      <div
        v-track-click="{
          name: BTN_EVTS.ACTIVE_CONVERSATION_SELECTED,
          selection: conversation.id,
        }"
        class="conversation-row"
        data-conversation-row
        @click="$emit('select', conversation)"
      >
        <div class="conversation-col-logo" data-logo>
          <img
            class="logo"
            data-ac-logo-img
            :src="conversation.logo || require('@/assets/img/shared/default/logo.png')"
          />
        </div>
        <div class="conversation-col-content" data-content>
          <TruncatedText v-if="conversation.title" class="title" maxlength="50" :text="conversation.title" />
          <TruncatedText v-if="conversation.subtitle" class="subtitle" maxlength="50" :text="conversation.subtitle" />
          <TruncatedText v-if="conversation.description" class="description" maxlength="100">
            {{ conversation.description }}
          </TruncatedText>
        </div>
        <div data-badge class="conversation-col-badge">
          <BasicBadge :hidden="!conversation.newMessages" :value="conversation.newMessages || 1" />
        </div>
      </div>
      <div class="conversation-time-ago">
        <FormatTime :date="conversation.updatedAt" time-ago format="time-ago" />
      </div>
    </div>
    <div class="action-container">
      <button v-track-click="BTN_EVTS.ACTIVE_CONVERSATION_SHOW_ALL" @click="$emit('show:all')">
        {{ $t('page.active_conversation.show_all') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import BasicBadge from '@/components/BasicBadge.vue';
import FormatTime from '@/components/FormatTime.vue';
import TruncatedText from '@/components/TruncatedText.vue';
import { Conversation } from './type';
import trackClick, { BTN_EVTS } from '@/directives/trackClick';

@Component({
  components: { BasicBadge, FormatTime, TruncatedText },
  directives: { trackClick },
})
export default class ConversationList extends Vue {
  @Prop({ default: () => [] }) readonly conversations!: Conversation[];

  readonly BTN_EVTS = BTN_EVTS;
}
</script>

<style lang="scss">
@import '@/assets/scss/base.scss';

.conversation {
  padding: 16px;
  background: $ac-home-background;

  .action-container {
    text-align: center;

    button {
      font-size: $size-m2;
      padding: 0.8em 7em;
      margin-top: 10px;
      color: $filled-button-text-color;
      background: $filled-button-background;
      border: none;
      cursor: pointer;

      &:active {
        border: none;
      }

      &.disabled {
        background: $filled-button-background--disabled;
        color: $filled-button-text-color--disabled;
        cursor: not-allowed;
      }
    }
  }

  .container-title {
    margin-bottom: 15px;
    padding-left: 3px;
    color: $ac-home-title;
    font-size: $size-m2;
    font-weight: 500;
  }
  .conversation-row-container {
    margin-bottom: 10px;
    .conversation-time-ago {
      text-align: right;
      margin-top: 4px;
      font-size: $size-xs;
      color: $ac-item-time-color;
      padding-right: 4px;
    }
  }
  .conversation-row {
    cursor: pointer;
    background: $ac-item-background;
    display: flex;
    gap: $size-xs;
    padding: 10px;
    box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
    border-radius: 10px;

    .conversation-col-logo {
      flex: 0 0 1;
      display: flex;
      align-items: center;
      justify-content: center;

      .logo {
        width: 30px;
      }
    }

    .conversation-col-content {
      flex-grow: 1;

      span:not(:last-child) {
        display: block;
        margin-bottom: 5px;
      }

      .title {
        font-weight: 600;
        font-size: $size-m;
        line-height: 1;
        color: $ac-item-title-color;
        white-space: pre-line;
        word-break: break-all;
      }
      .subtitle {
        color: $ac-item-subtitle-color;
        font-size: $size-xs;
        white-space: pre-line;
        word-break: break-all;
      }

      .description {
        color: $ac-item-description-color;
        font-size: $size-m2;
        white-space: pre-line;
        word-break: break-all;
      }
    }

    .conversation-col-badge {
      flex: 0 0 1;
      display: flex;
      align-items: center;
    }
  }
}
</style>
